/**
 * Lifetime Deal — Dashboard upsell card (Phase 6 — Stripe one-time upgrade).
 *
 * Visibility is server-enforced via GET /api/lifetime/me. The card renders
 * ONLY when the backend returns `should_show_upsell=true`, which is
 * restricted to LTD `tier_1` (at cap) and `tier_2` (near cap) tenants.
 *
 * Click "Upgrade Lifetime" → opens preview modal → "Pay $99" launches a
 * real Stripe Checkout session. On return (`?lifetime_upgrade_session=...`)
 * Dashboard polls the status endpoint and refreshes the license.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Sparkles, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { lifetimeAPI } from '../lib/api';

const TIER_LABEL = { tier_1: 'Tier 1', tier_2: 'Tier 2' };

export default function LifetimeUpsellCard() {
  const [info, setInfo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [pollMessage, setPollMessage] = useState('');

  const reload = useCallback(async () => {
    try {
      const { data } = await lifetimeAPI.me();
      setInfo(data);
      return data;
    } catch {
      setInfo(null);
      return null;
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // After Stripe returns with ?lifetime_upgrade_session=... — poll status.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('lifetime_upgrade_session');
    const cancelled = params.get('lifetime_upgrade_cancelled');
    if (cancelled) {
      toast.info('Upgrade cancelled. You can try again whenever you’re ready.');
      params.delete('lifetime_upgrade_cancelled');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      return;
    }
    if (!sessionId) return;

    let cancelledPolling = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;
    setPollMessage('Confirming your upgrade…');

    const poll = async () => {
      if (cancelledPolling || attempts >= MAX_ATTEMPTS) {
        setPollMessage('');
        if (!cancelledPolling) {
          toast.error('Still confirming your payment. Refresh in a moment.');
        }
        return;
      }
      attempts += 1;
      try {
        const { data } = await lifetimeAPI.upgradeStatus(sessionId);
        if (data.payment_status === 'paid') {
          await reload();
          setPollMessage('');
          toast.success('Welcome to Unlimited Lifetime!');
          // Strip the query param so reload won't re-poll.
          const p = new URLSearchParams(window.location.search);
          p.delete('lifetime_upgrade_session');
          const qs = p.toString();
          window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
          return;
        }
        if (data.status === 'expired') {
          setPollMessage('');
          toast.error('Checkout expired — please try again.');
          return;
        }
      } catch {
        // soft-fail; retry
      }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelledPolling = true; };
  }, [reload]);

  const handleUpgrade = async () => {
    setRedirecting(true);
    try {
      const { data } = await lifetimeAPI.upgradeCheckout();
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('Could not start checkout. Please try again.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setRedirecting(false);
    }
  };

  if (pollMessage) {
    return (
      <Card data-testid="lifetime-upsell-polling" className="border-orange-200 bg-orange-50">
        <CardContent className="p-4 flex items-center gap-3">
          <Loader2 size={18} className="animate-spin text-orange-600" />
          <p className="text-sm text-slate-700">{pollMessage}</p>
        </CardContent>
      </Card>
    );
  }

  if (!info || !info.should_show_upsell || dismissed) return null;

  const tierLabel = TIER_LABEL[info.tier] || info.tier;

  return (
    <>
      <Card
        data-testid="lifetime-upsell-card"
        className="border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 shadow-sm"
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
            <div className="w-11 h-11 rounded-xl bg-orange-500/10 text-orange-600 flex items-center justify-center shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3
                  data-testid="lifetime-upsell-title"
                  className="text-lg font-semibold text-slate-900"
                  style={{ fontFamily: 'Manrope' }}
                >
                  Need More Locations?
                </h3>
                <button
                  type="button"
                  onClick={() => setDismissed(true)}
                  className="text-slate-400 hover:text-slate-600 -mt-1"
                  aria-label="Dismiss"
                  data-testid="lifetime-upsell-dismiss"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Upgrade once. Unlock everything.
              </p>
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                <li className="flex items-center gap-2"><Check size={14} className="text-green-600 shrink-0" /> Unlimited locations</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-600 shrink-0" /> Unlimited staff</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-600 shrink-0" /> Future updates included</li>
              </ul>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="text-right">
                <p className="text-xs text-slate-500">One-time</p>
                <p className="text-2xl font-bold text-slate-900">$99</p>
              </div>
              <Button
                type="button"
                onClick={() => setModalOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="lifetime-upsell-cta"
              >
                Upgrade Lifetime
              </Button>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">
                Current plan: {tierLabel}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={(v) => !redirecting && setModalOpen(v)}>
        <DialogContent className="max-w-md" data-testid="lifetime-upgrade-modal">
          <DialogHeader>
            <DialogTitle>Upgrade to Unlimited Lifetime</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500">Current Plan</p>
                <p className="font-semibold text-slate-900" data-testid="lifetime-modal-current-plan">{tierLabel}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Locations Used</p>
                <p className="font-semibold text-slate-900" data-testid="lifetime-modal-locations">
                  {info.locations_used} / {info.locations_max}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <p className="text-xs uppercase tracking-wide text-orange-600 font-semibold">Upgrade</p>
              <p className="text-lg font-bold text-slate-900 mt-1">Unlimited Locations</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Unlimited locations</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Unlimited staff</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Future updates included</li>
              </ul>
            </div>

            <div className="flex items-baseline justify-between">
              <span className="text-sm text-slate-600">Price</span>
              <span className="text-2xl font-bold text-slate-900">$99 <span className="text-sm font-normal text-slate-500">one-time</span></span>
            </div>
            <p className="text-xs text-slate-500">
              You’ll be redirected to Stripe to complete payment. Your account upgrades the moment the charge succeeds.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={redirecting}
              data-testid="lifetime-modal-close"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpgrade}
              disabled={redirecting}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="lifetime-modal-pay"
            >
              {redirecting ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
              {redirecting ? 'Redirecting…' : 'Pay $99'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
